import { config } from '../../../../../config/config.json';

type StreamChunk = {
  content: string
  is_last_chunk: boolean
  is_last_message?: boolean
  error?: boolean
};

type StreamParams = {
  token: string
  userMessage: string | null
  onChunk: (chunk: StreamChunk) => void
  onError?: (err: any) => void
  abortSignal?: AbortSignal
};

const AI_ROLE_PLAY_API_BASE_URL = config.aiRolePlay.aiHostUrl;

const streamAIResponse = async ({
  token,
  userMessage,
  onChunk,
  onError,
  abortSignal,
}: StreamParams) => {
  try {
    const response = await fetch(`${AI_ROLE_PLAY_API_BASE_URL}/role-play/learner/stream`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_message: userMessage,
      }),
      signal: abortSignal,
    });

    if (!response.ok || !response.body) {
      throw new Error('Failed to connect to AI stream');
    }

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();

    let buffer = '';

    const processLines = (lines: string[]): boolean => {
      let shouldCancel = false;
      lines
        .map((line) => line.trim())
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.replace(/^data:\s*/, ''))
        .forEach((jsonString) => {
          try {
            const chunk: StreamChunk = JSON.parse(jsonString);
            onChunk(chunk);

            if (chunk.error) {
              shouldCancel = true;
              onError?.(new Error(`Error from AI stream: ${chunk.content}`));
            }
          } catch (err) {
            onError?.(err);
          }
        });

      return shouldCancel;
    };

    const readNextChunk = (): Promise<void> => reader.read().then(({ value, done }) => {
      if (done) return Promise.resolve();

      buffer += value;

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      const shouldCancel = processLines(lines);

      if (shouldCancel) {
        return reader.cancel();
      }

      return readNextChunk();
    });

    await readNextChunk();
  } catch (err) {
    // Don't call onError or re-throw for aborted requests - this is intentional cancellation
    if (err instanceof Error && err.name === 'AbortError') {
      return;
    }
    onError?.(err);
    // Re-throw so callers can add their own .catch() for additional cleanup logic
    // e.g., handleStartConversation needs to reset status to NOT_STARTED and clear opening narrative
    throw err;
  }
};

export default streamAIResponse;
